import type { TripDetailsV2 } from "@b2bportal/air-shopping-api";
import type { Airport } from "@b2bportal/air-shopping-api";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { SortOptionEnum } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { FlightListGridFilter, FlightSummary, Sort } from "@tenant/flights";
import { Icon } from "@tenant/ui";
import { PackageBreadcrumbs } from "../PackageBreadcrumbs";
import { PackageFlightFilterDialog } from "../PackageFlightFilterDialog";
import { usePackageFlightReturnPageHeader } from "./hook";
import styles from "./PackageFlightReturnPageHeader.module.scss";
import { PackagePriceSummary } from "@/pages/Packages/components";
import { CombinedPackageSteps } from "@/pages/Packages/hooks/usePackageFlow";

interface PackageFlightReturnPageHeaderProps {
  sortOption: SortOptionEnum;
  handleSortChange: (value: SortOptionEnum) => void;
  tripDetails: TripDetailsV2 | null;
  airports: { [key: string]: Airport };
}

export const PackageFlightReturnPageHeader = ({
  sortOption,
  handleSortChange,
  tripDetails,
  airports,
}: PackageFlightReturnPageHeaderProps) => {
  const [block, cn] = useModuleBEM(styles, "PackageFlightReturnPageHeader");
  const { t } = useI18nContext();
  const { title, routeInfo } = usePackageFlightReturnPageHeader();
  return (
    <div className={block}>
      <PackageBreadcrumbs
        className={cn("breadcrumbs")}
        currentStep={CombinedPackageSteps.FLIGHT_RETURN}
      />
      <div className={cn("content")}>
        <div className={cn("left-content")}>
          <h1 className={cn("title")}>{title}</h1>

          {routeInfo && (
            <div className={cn("route-info")}>
              <span className={cn("route-airports")}>
                {routeInfo.origin}{" "}
                <Icon
                  className={cn("route-icon")}
                  iconName={IconNameEnum.chevronRight}
                />
                {routeInfo.destination}
              </span>
              <span className={cn("route-separator")}>•</span>
              <span className={cn("route-date")}>{routeInfo.date}</span>
            </div>
          )}
        </div>
        <div className={cn("right-content")}>
          {tripDetails && (
            <FlightSummary
              tripDetails={tripDetails}
              airports={airports}
              sliceIndex={0}
              className={cn("flight-summary")}
            />
          )}
          <PackagePriceSummary />
        </div>
      </div>
      <div className={cn("content")}>
        <div className={cn("left-content")}>
          <p className={cn("trip-category")}>
            <span className={cn("bold")}>{t("flightRoundTrip")}</span>
            {` | ${t("core-flights.flightShopTitle.roundTripSubtitle")}`}
          </p>
        </div>
        <div className={cn("right-content")}>
          <div className={cn("controls-row")}>
            <div className={cn("sort-container")}>
              <Sort value={sortOption} handleSortChange={handleSortChange} />
            </div>
            <div className={cn("filters-container")}>
              {/* Departure page is 0, return page is 1 */}
              <PackageFlightFilterDialog sliceIndex={1} />
            </div>
          </div>
        </div>
      </div>
      <div className={cn("grid-filter-container")}>
        <FlightListGridFilter className={cn("grid-filter")} />
      </div>
    </div>
  );
};
